import {getUsername} from '@/filters/username';
import {weekDays} from '@/library/date';
import store from '@/store';
import {markRaw} from 'vue';
import dateHelper from '../../../utils/portuna/dateHelper';
import CalendarExportList from '../../../views/calendar/CalendarExportList.vue';
import CalendarICSExportPopup from '../../../views/calendar/CalendarICSExportPopup.vue';
import CalendarSyncPopup from '../../../views/calendar/CalendarSyncPopup.vue';
import {exportEnabled} from '../../export';
import {toInt} from '../../math';
import {Popup, popupDefaultCloseButton} from '../../popup';
import {arrayIntersect} from '../../utils';
import {isServicesCalenderEntry} from '../services/services';

export const DEFAULT_HOUR_HEIGHT_PIXELS = 60;

export const serviceType = 'services';

export const viewTypeOptions: ViewTypeOptions = {
    day: {
        name: 'Tag',
        icon: 'view_day',
        title: 'Zur Tages-Ansicht',
    },
    week: {
        name: 'Woche',
        icon: 'view_week',
        title: 'Zur Wochen-Ansicht',
    },
    '5days': {
        name: '5 Tage',
        icon: 'view_week',
        title: 'Zur 5-Tages-Ansicht',
    },
    '7days': {
        name: '7 Tage',
        icon: 'view_week',
        title: 'Zur 7-Tages-Ansicht',
    },
    month: {
        name: 'Monat',
        icon: 'event',
        title: 'Zur Monats-Ansicht',
    },
    table: {
        name: 'Liste',
        icon: 'list',
        title: 'Zur Listen-Ansicht',
    },
    tile: {
        name: 'Kachel',
        icon: 'list',
        title: 'Zur Kachel-Ansicht',
    },
};

export const durationsOptions: SelectOption[] = [
    {
        label: '15 Minuten',
        value: 15,
    }, {
        label: '30 Minuten',
        value: 30,
    }, {
        label: '45 Minuten',
        value: 45,
    }, {
        label: '60 Minuten',
        value: 60,
    }, {
        label: '75 Minuten',
        value: 75,
    }, {
        label: '90 Minuten',
        value: 90,
    }, {
        label: '105 Minuten',
        value: 105,
    }, {
        label: '120 Minuten',
        value: 120,
    }, {
        label: '135 Minuten',
        value: 135,
    }, {
        label: '150 Minuten',
        value: 150,
    }, {
        label: '165 Minuten',
        value: 165,
    }, {
        label: '180 Minuten',
        value: 180,
    },
];

export const durationsOptionsWithInput: SelectOption[] = [
    {
        label: 'Eigene Eingabe',
        value: 0,
    }, {
        label: '15 Minuten',
        value: 15,
    }, {
        label: '30 Minuten',
        value: 30,
    }, {
        label: '45 Minuten',
        value: 45,
    }, {
        label: '60 Minuten',
        value: 60,
    }, {
        label: '75 Minuten',
        value: 75,
    }, {
        label: '90 Minuten',
        value: 90,
    }, {
        label: '105 Minuten',
        value: 105,
    }, {
        label: '120 Minuten',
        value: 120,
    }, {
        label: '135 Minuten',
        value: 135,
    }, {
        label: '150 Minuten',
        value: 150,
    }, {
        label: '165 Minuten',
        value: 165,
    }, {
        label: '180 Minuten',
        value: 180,
    },
];

export const noCategoryResources = [
    'meetingservice',
    'councilservice',
    'shift-schedule',
    'absences',
];

function getEntryTimeString(entry: CalendarEntryListData, withBreak = true, fullDate = false)
{
    let breakSeparator: string = withBreak ? '<br>' : ' ';
    let today                  = fullDate ? null : dateHelper('Y-m-d', 'today');
    let tomorrow               = fullDate ? null : dateHelper('Y-m-d', 'tomorrow');

    const start = entry.recurrence_start || entry.entry_start;
    const end   = entry.recurrence_end || entry.entry_end;

    let startString = today === start
        ? `<span class="date-hint date-hint-today">Heute</span>`
        : (
            tomorrow === start
                ? `<span class="date-hint date-hint-tomorrow">Morgen</span>`
                : `<span class="date-hint">${dateHelper('D, d.m.Y', start)}</span>`
        );

    let endString = today === end || start === end
        ? ''
        : (
            tomorrow === end
                ? `<span class="date-hint date-hint-tomorrow">Morgen</span>`
                : `<span class="date-hint">${dateHelper('D, d.m.Y', end)}</span>`
        );

    let time = startString;

    const hasDuration = end && '0000-00-00' !== end && start !== end;
    if (entry.all_day || (entry.day_start_time && entry.day_start_time.match(/00:00(:\d{2})?/) && entry.day_end_time && entry.day_end_time.match(/24:00(:\d{2})?/)))
    {
        time += (hasDuration && endString
                ? ' - ' + endString
                : '')
            + (entry.resource !== 'task' ? `${breakSeparator}(<strong>Ganzt&auml;gig</strong>)` : '');
    }
    else
    {
        // append start time if not empty
        entry.entry_start_time && (time += (!hasDuration && withBreak ? breakSeparator : ', ') + entry.entry_start_time.substring(0, 5) + (hasDuration ? ' Uhr -' : ''));
        // append entry end date if not empty and different day
        time += hasDuration
            ? (
                breakSeparator + endString
                // append entry end time, if not empty, to entry end
                + (entry.entry_end_time ? (endString ? ', ' : '') + entry.entry_end_time.substring(0, 5) + ' Uhr' : '')
            )
            // append entry end to start date / time, if no end date is given
            : (entry.entry_end_time ? ' - ' + entry.entry_end_time.substring(0, 5) : '') + (entry.entry_start_time || entry.entry_end_time ? ' Uhr' : '');
    }

    return time;
}

class Calendar
{
    private readonly entry: CalendarData;

    private bookableOptions: Nullable<BookableMultiple> = null;

    private bookableOptionsByDate: Record<string, BookableDayOptions> = {};

    constructor(entry: CalendarData)
    {
        this.entry = entry;
    }

    static calcHeight(startTime: string, endTime: null | string)
    {
        let height = store.state.calendarHourHeight / 2;

        if (null !== endTime)
        {
            let startH = parseInt(startTime.substr(0, 2)),
                startM = parseInt(startTime.substr(3, 2)),
                endH   = parseInt(endTime.substr(0, 2)),
                endM   = parseInt(endTime.substr(3, 2));

            let hours   = endH - startH,
                minutes = endM - startM;

            if (minutes < 0)
            {
                hours--;
                if (minutes !== 0)
                {
                    minutes += 60;
                }
            }

            height = hours * store.state.calendarHourHeight + minutes * store.getters.calendarMinuteHeight;
        }

        return height;
    };

    //format: from "06:30", to: "15:00"
    static calcWidth(from: string, to: string, returnRaw: boolean = false)
    {
        let startValue = parseInt(from.substr(0, 2)) + parseInt(from.substr(3, 2)) / 60;
        let endValue   = parseInt(to.substr(0, 2)) + parseInt(to.substr(3, 2)) / 60;

        return returnRaw ? (endValue - startValue) / 24 : (endValue - startValue) / 24 * 100 + '%';
    }

    static calcLeft(from: string)
    {
        let startValue = parseInt(from.substr(0, 2)) + parseInt(from.substr(3, 2)) / 60;

        return startValue / 24 * 100 + '%';
    }

    static calcTop(startTime: string)
    {
        let hours   = parseInt(startTime.substr(0, 2)),
            minutes = parseInt(startTime.substr(3, 2));

        return hours * store.state.calendarHourHeight + minutes * store.getters.calendarMinuteHeight;
    };

    static calcEntryTime(obj: CalendarEntryListData)
    {
        let startTime: Nullable<string> = null,
            endTime: Nullable<string>   = null,
            startHour: string | number,
            startMinute: string | number,
            endHour: string | number,
            endMinute: string | number;

        if (1 == obj.all_day)
        {
            startTime            = '00:00';
            endTime              = '24:00';
            obj.entry_start_time = startTime;
            obj.entry_end_time   = endTime;
        }
        else if (null !== obj.entry_start_time || null !== obj.entry_end_time)
        {
            if (null !== obj.entry_start_time && null !== obj.entry_end_time)
            {
                startTime = obj.entry_start_time.substr(0, 5);
                endTime   = obj.entry_end_time.substr(0, 5);
            }
            else if (null !== obj.entry_start_time && null === obj.entry_end_time)
            {
                startTime = obj.entry_start_time.substr(0, 5);
                if (1 === obj.recurrence_day)
                {
                    endTime = startTime;
                }
                else
                {
                    endTime = '24:00';
                }
            }
            else if (null === obj.entry_start_time && null !== obj.entry_end_time)
            {
                startTime = '00:00';
                endTime   = obj.entry_end_time.substr(0, 5);
            }

            // override start and / or end time
            //+ with special conditions
            if (startTime === endTime && obj.recurrence_start === obj.recurrence_end)
            {
                startTime   = obj.entry_start_time!.substr(0, 5);
                startHour   = ~~obj.entry_start_time!.substr(0, 2);
                startMinute = ~~obj.entry_start_time!.substr(3, 2);

                endHour   = startHour;
                endMinute = startMinute + 60;

                while (endMinute >= 60)
                {
                    endHour += 1;
                    endMinute -= 60;
                }

                if (endHour >= 24)
                {
                    endHour   = 24;
                    endMinute = 0;
                }

                endHour   = ('0' + endHour).slice(-2);
                endMinute = ('0' + endMinute).slice(-2);

                endTime = endHour + ':' + endMinute;
            }

            if (obj.recurrence_day && obj.recurrence_day > 1)
            {
                startTime = '00:00';
            }

            if (obj.recurrence_day && obj.recurrence_day <= toInt(obj.duration))
            {
                endTime = '24:00';
            }
        }
        else
        {
            obj.all_day          = 1;
            startTime            = '00:00';
            endTime              = '24:00';
            obj.entry_start_time = startTime;
            obj.entry_end_time   = endTime;
        }

        let $return = {
            start: startTime as string,
            end: endTime as string,
        };

        obj.day_start_time = $return.start;
        obj.day_end_time   = $return.end;

        return $return;
    };

    static calcTimeByPixel(pixel: number, minuteStep: number)
    {
        let h = Math.floor(pixel / store.state.calendarHourHeight),
            m = Math.floor((pixel % store.state.calendarHourHeight) / (minuteStep * store.getters.calendarMinuteHeight)) * minuteStep;

        while (m >= 60)
        {
            h++;
            m -= 60;
        }

        return ('0' + h).slice(-2) + ':' + ('0' + m).slice(-2);
    };

    static calendarTooltip(calendar: CalendarData, syncActive: Nullable<boolean> = null): string
    {
        if (exportEnabled())
        {
            return '';
        }

        const tooltipRows = [];
        let tooltip       = '';

        tooltipRows.push('<strong>Titel:</strong> ' + calendar.title);
        if (!exportEnabled())
        {
            const creator = 'number' === typeof calendar.creator ? getUsername(calendar.creator) : calendar.creator;
            tooltipRows.push('<strong>Erstellt:</strong> ' + dateHelper('d.m.Y, H:i', calendar.created) + ' (' + creator + ')');

            if (calendar.edited && calendar.edited !== calendar.created)
            {
                const editor = 'number' === typeof calendar.editor ? getUsername(calendar.editor) : calendar.editor;
                tooltipRows.push('<strong>Bearbeitet:</strong> ' + dateHelper('d.m.Y, H:i', calendar.edited) + ' (' + editor + ')');
            }
        }
        if (calendar.contacts && calendar.contacts.length)
        {
            const contacts = calendar.contacts.map((user) => getUsername(user));
            tooltipRows.push('<strong>Ansprechpartner:</strong> ' + contacts.join(' '));
        }

        if (null !== syncActive)
        {
            const syncClass = syncActive ? 'text-success' : 'text-danger';
            const syncIcon  = syncActive ? 'published_with_changes' : 'sync_disabled';
            const syncText  = syncActive ? 'Aktiv' : 'Inaktiv';

            tooltipRows.push('<strong>Kalender-Sync:</strong> '
                + `<span class="${syncClass}">${syncText}</span> <i class="material-icons font-size-14 ${syncClass}" aria-hidden="true">${syncIcon}</i>`);
        }

        if (calendar.preview && calendar.preview.id)
        {
            tooltip = '<div style="max-width: 390px; float: left;"><img src="'
                + calendar.preview.paths.preview
                + '" style="max-width: 390px; float: left; margin-right: 10px;" /></div>';
            tooltip += '<div style="max-width: 400px; float: left;">' + tooltipRows.join('<br>') + '</div>';
        }
        else
        {
            tooltip = tooltipRows.join('<br>');
        }

        return tooltip;
    }

    static entryTooltip(entry: CalendarEntryListData, options: CalendarResourceOptions | ServiceCalendarResourceOptions, serviceNames: Nullable<String[]>): string
    {
        let time,
            title;

        if (entry.blocked_info && !entry.is_allowed_view)
        {
            time = entry.blocked_info;
        }
        else
        {
            time = this.parseEntryTime(entry);
        }

        title = '';
        title += entry.request ? '<i class="material-icons md-16" aria-hidden="true" v-if="entry.request">new_releases</i>' : '';

        if (entry.resource === serviceType && !entry.is_anonymized)
        {
            title += ' <span><strong>';

            if (entry.is_reservation)
            {
                if (isServicesCalenderEntry(entry))
                {
                    title += 'Unbestätigte Reservierungsanfrage';
                }
                else
                {
                    title += 'Reservierung';
                }
            }
            else if (!entry.prename)
            {
                title += ' <span><strong>' + entry.title + '</strong></span>';
            }
            else
            {
                title += (entry.salutation ? entry.salutation + ' ' : '') + entry.prename + ' ' + entry.name;
            }

            title += '</strong></span>';

        }
        else if (entry.title)
        {
            title += ' <span><strong>' + entry.title + '</strong></span>';
        }

        // append request information
        title += entry.request ? '<br><em>Reservierungsanfrage</em>' : '';
        // append deactivated information
        title += entry.deactivated ? '<br><em>Deaktiviert</em>' : '';
        // append not like info for events
        if (entry.resource === 'event' && entry.canLike && entry.userInvited && entry.likeStatus === 0)
        {
            title += '<br><em>Noch nicht zugesagt</em>';
        }
        // append entry time
        if (entry.resource === serviceType && entry.is_anonymized && isServicesCalenderEntry(entry))
        {
            title += time;
        }
        else
        {
            title += '<br>' + time;
        }

        // append calendar title information
        if (entry.calendar_title && entry.resource !== serviceType)
        {
            title += '<br>' + entry.calendar_title;
        }

        // append category name
        if (entry.category_name && entry.resource !== serviceType)
        {
            let categoryTerm = 'Kategorie';

            if (entry.resource && noCategoryResources.includes(entry.resource))
            {
                categoryTerm = 'Modul';
            }

            title += '<br>' + categoryTerm + ': ' + entry.category_name;
        }

        // append service name(s)
        if (serviceNames && entry.resource === serviceType)
        {
            if (entry.services && entry.services.length)
            {
                title += '<br>'
                    + (entry.services.length > 1 ? (<ServiceCalendarResourceOptions>options).serviceTypePlural : (<ServiceCalendarResourceOptions>options).serviceType)
                    + ': ' + serviceNames.join(', ');
            }
            else if (entry.is_anonymized && isServicesCalenderEntry(entry))
            {
                title += '<br> Ohne ' + (<ServiceCalendarResourceOptions>options).serviceType;
            }
        }

        if (entry.creator_name)
        {
            title += '<br>Erstellt von ' + entry.creator_name;
        }

        return title;
    }

    static parseEntryTime(entry: CalendarEntryListData)
    {
        let time: string = dateHelper('d.m.Y', entry.recurrence_start) as string;

        if (entry.all_day || (entry.day_start_time && entry.day_end_time && entry.day_start_time.match(/00:00(:\d{2})?/) && entry.day_end_time.match(/24:00(:\d{2})?/)))
        {
            time += (entry.recurrence_end && '0000-00-00' !== entry.recurrence_end && entry.recurrence_start !== entry.recurrence_end
                    ? ' - ' + dateHelper('d.m.Y', entry.recurrence_end) + '<br>'
                    : '')
                + ' (<strong>Ganzt&auml;gig</strong>)';
        }
        else
        {
            // append start time if not empty
            entry.entry_start_time && (time += ', ' + entry.entry_start_time.substring(0, 5) + ' Uhr');
            // append entry end date if not empty and different day
            time += entry.recurrence_end && '0000-00-00' !== entry.recurrence_end && entry.recurrence_start !== entry.recurrence_end
                ? '<br>' + dateHelper('d.m.Y', entry.recurrence_end)
                // append entry end time, if not empty, to entry end
                + (entry.entry_end_time ? ', ' + entry.entry_end_time.substring(0, 5) + ' Uhr' : '')
                // append entry end to start date / time, if no end date is given
                : (entry.entry_end_time ? ' bis ' + entry.entry_end_time.substring(0, 5) + ' Uhr' : '');
        }

        return time;
    }

    static getEntriesDurationInSeconds(entry: CalendarEntryListData)
    {
        let startDate = dateHelper('U', entry.recurrence_start + ' ' + (entry.entry_start_time || entry.day_start_time)) as number;
        let endDate   = dateHelper('U', entry.recurrence_end + ' ' + (entry.entry_end_time || entry.day_end_time)) as number;

        return endDate - startDate;
    }

    setBookableOptions(options: Nullable<BookableMultiple>)
    {
        this.bookableOptions       = options;
        this.bookableOptionsByDate = {};
    }

    getBookableRangeByDay(weekDay: number, limit: boolean = true, bookables: Nullable<BookableRanges> = null)
    {
        let options = bookables ? bookables : (this.bookableOptions ? this.bookableOptions : this.entry);

        if (options.hasOwnProperty('bookable'))
        {
            options = options['bookable'];
        }

        let bookable: BookableDayOptions = {
            is_bookable: true,
            time_range: [
                {
                    from: '00:00',
                    to: '24:00',
                },
            ],
        };

        if (limit)
        {
            const dayInfo = weekDays[weekDay - 1];

            if (dayInfo && options[dayInfo.key])
            {
                const dayOptions = options[dayInfo.key];

                bookable.is_bookable = !!dayOptions.is_bookable;

                if (bookable.is_bookable)
                {
                    bookable.time_range[0].from = dayOptions.time_range[0].from || '00:00';
                    bookable.time_range[0].to   = dayOptions.time_range[0].to || '24:00';
                }
            }
        }

        // flawed, may cause endless reactive update loop
        this.bookableOptionsByDate[weekDay] = bookable;

        return this.bookableOptionsByDate[weekDay];
    }

    getBookableRangeOptions(startTime: string, endTime: string)
    {
        return {
            show: true,
            styles: {
                top: '' + Calendar.calcTop(startTime) + 'px',
                height: '' + Calendar.calcHeight(startTime, endTime) + 'px',
            },
        };
    }

    getBookableScheduleRangeOptions(startTime: string, endTime: string)
    {
        return {
            show: true,
            styles: {
                left: '' + Calendar.calcTop(startTime) + 'px',
                width: '' + Calendar.calcHeight(startTime, endTime) + 'px',
            },
        };
    }

    isInDisabledPart(start: Nullable<string>, end: Nullable<string>, weekday: number)
    {
        let bookable = this.getBookableRangeByDay(weekday);
        let result   = false;

        for (let i = 0; i < bookable.time_range.length; i++)
        {
            let range = bookable.time_range[i];
            result    = !!(!result && (!bookable['is_bookable'] || ((start && start.substr(0, 5) < (range['from'] ? range['from'] : '00:00')) || (end
                && end.substr(0, 5)
                > (range['to'] ? range['to'] : '23:59')))));
        }

        return this.entry.bookable_limit && result;
    };

    calcColumnData(entry: CalendarEntryListData, columnData: ColumnDataDay)
    {
        let timeData: TimeData = entry.__timeData!,
            startHour          = parseInt(timeData.start.substr(0, 2)),
            startMinute        = parseInt(timeData.start.substr(3, 2)),
            startTime          = timeData.start,
            endTime            = timeData.end;

        if (!entry.date)
        {
            return;
        }

        do
        {
            if (startMinute >= 60)
            {
                startMinute = 0;
                startHour++;
            }

            startTime = ('0' + startHour).slice(-2) + ':' + ('0' + startMinute).slice(-2);

            if (startTime !== endTime)
            {
                if ('undefined' !== typeof columnData[startTime])
                {
                    columnData[startTime].totalColumns += 1;
                    columnData[startTime].entries.push(entry.id);
                }
                else
                {
                    columnData[startTime] = {
                        totalColumns: 1,
                        usedColumns: {},
                        entries: [entry.id],
                    };
                }
            }

            startMinute += 1;
        }
        while (startTime < endTime);
    };

    optimiseColumnDataByEntry(entry: number, time: string, columnData: ColumnDataDay)
    {
        let startHour   = parseInt(time.substr(0, 2));
        let startMinute = parseInt(time.substr(3, 2));

        let maxColumns      = 0;
        let startTime       = time;
        let endTime         = time;
        let affectedEntries = [entry];
        do
        {
            if (startMinute >= 60)
            {
                startMinute = 0;
                startHour++;
            }
            time = ('0' + startHour).slice(-2) + ':' + ('0' + startMinute).slice(-2);

            if ('undefined' === typeof columnData[time] || 0 === arrayIntersect(affectedEntries, columnData[time].entries).length)
            {
                break;
            }

            affectedEntries = affectedEntries.concat(...columnData[time].entries);

            endTime    = time;
            maxColumns = Math.max(columnData[time].totalColumns, maxColumns);

            startMinute += 1;
        }
        while (true);

        if (startTime !== endTime && maxColumns > 1)
        {
            startHour   = parseInt(startTime.substr(0, 2));
            startMinute = parseInt(startTime.substr(3, 2));

            do
            {
                if (startMinute >= 60)
                {
                    startMinute = 0;
                    startHour++;
                }
                time = ('0' + startHour).slice(-2) + ':' + ('0' + startMinute).slice(-2);

                columnData[time].totalColumns = maxColumns;

                startMinute += 1;
            }
            while (endTime > time);
        }
    }

    optimiseColumnData(columnData: ColumnDataDay)
    {
        let optimised: number[] = [];
        for (const time in columnData)
        {
            if (!columnData.hasOwnProperty(time) || columnData[time].entries.length <= 1)
            {
                continue;
            }

            for (let entry of columnData[time].entries.reverse())
            {
                if (!optimised.includes(entry))
                {
                    this.optimiseColumnDataByEntry(entry, time, columnData);
                    optimised.push(entry);
                }
            }
        }

    };

    getEntryLayoutData(entry: CalendarEntryListData, useColumnData: boolean = true, dateData: ColumnDataDay): LayoutProperties
    {
        let timeData: TimeData = entry.__timeData!,
            startTime          = timeData.start,
            endTime            = timeData.end,
            totalColumns       = 0,
            column             = 1,
            endColumn          = 1,
            columnCalculated   = false,
            classes            = [],
            data: LayoutProperties;

        classes.push('calendar-time-entry-wrapper');

        if (entry.date && useColumnData && dateData)
        {
            // calculate column and end column of the entry
            if (dateData)
            {

            }

            while (!columnCalculated)
            {
                if ('undefined' === typeof dateData[startTime].usedColumns[column])
                {
                    columnCalculated = true;
                    endColumn        = column;

                    while ('undefined' === typeof dateData[startTime].usedColumns[endColumn + 1] && endColumn + 1 <= dateData[startTime].totalColumns)
                    {
                        endColumn++;
                    }
                }
                else
                {
                    column++;
                }
            }

            // calculate the total columns for the entry - needed for width and left position
            for (let time in dateData)
            {
                if (!dateData.hasOwnProperty(time))
                {
                    continue;
                }
                let data = dateData[time];

                if (time >= startTime && time < endTime)
                {
                    totalColumns             = Math.max(totalColumns, data.totalColumns);
                    data.totalColumns        = totalColumns;
                    data.usedColumns[column] = true;
                }

                if (time !== endTime)
                {
                    while ('undefined' !== typeof data.usedColumns[endColumn] && endColumn >= column)
                    {
                        endColumn--;
                    }

                    endColumn = endColumn < column || endColumn >= totalColumns || endColumn < 1 ? column : endColumn;
                }
            }

            classes.push('column-total-' + totalColumns);
            classes.push('column-start-' + column);
            classes.push('column-end-' + endColumn);

            if (totalColumns > 6)
            {
                classes.push('entry-no-text');
            }

            data = {
                width: parseFloat((100 / totalColumns * (endColumn - column + 1)).toFixed(2)),
                left: parseFloat((100 / totalColumns * (column - 1)).toFixed(2)),
                column: column,
                endColumn: endColumn,
                totalColumns: totalColumns,
                classes: classes,
                top: Calendar.calcTop(startTime),
                height: Calendar.calcHeight(startTime, endTime),
            };
        }
        else
        {
            data = {
                width: 96,
                left: 2,
                column: 1,
                endColumn: 1,
                totalColumns: 100,
                classes: classes,
                top: Calendar.calcTop(startTime),
                height: Calendar.calcHeight(startTime, endTime),
            };
        }

        entry.__layoutData = data;
        return data;
    }
}

export function exportCalendarPopUp(calendarEntryId: number, resource: string)
{
    new Popup({
        title: 'Veröffentlichungen',
        size: 'small',
        titleIcon: 'language',
        buttons: [popupDefaultCloseButton],
        component: CalendarExportList,
        componentProps: {
            id: calendarEntryId,
            resource,
            inPopUp: true,
        },
    }).open();
}

export function calendarSyncPopUp(calendarEntryId: number, resource: string, events: any = {})
{
    const popup = new Popup({
        title: 'Kalender-Sync',
        size: 'small',
        titleIcon: 'sync',
        buttons: [popupDefaultCloseButton],
        component: markRaw(CalendarSyncPopup),
        componentProps: {
            id: calendarEntryId,
            resource,
            inPopUp: true,
        },
        events
    });

    popup.open();

    return popup;
}

export function calendarICSExportPopup(calendarEntryId: number, resource: string, fetchUrl: string = '')
{
    const popup = new Popup({
        title: 'ICS-Export',
        size: 'small',
        titleIcon: 'import_export',
        buttons: [popupDefaultCloseButton],
        component: markRaw(CalendarICSExportPopup),
        componentProps: {
            id: calendarEntryId,
            resource,
            fetchUrl,
        },
    });

    popup.open();

    return popup;
}

export function getCalendarViewParams(viewType: string | 'day' | 'week' | '5days' | '7days' | 'list' | 'table', dateInformation: DateInformation): Record<string, string | number>
{
    if (viewType === 'month')
    {
        return {
            year: dateInformation.year,
            month: dateInformation.month,
        };
    }

    if (viewType === 'week' || viewType === '5days')
    {
        const [year, week] = (<string>dateHelper('Q', dateInformation.date)).split('W').map(value => parseInt(value));

        let params: Record<string, string | number> = {
            year,
            week,
        };

        if (viewType === '5days')
        {
            params['5days'] = 1;
        }

        return params;
    }

    let params: Record<string, string | number> = {
        year: dateInformation.year,
        month: dateInformation.month,
        day: dateInformation.day,
    };

    if (viewType === '7days')
    {
        params['7days'] = 1;
    }

    return params;
}

export {getEntryTimeString, Calendar};